import React from "react";
import AccordionEditable from "../../../../../components/ui/accordions/AccordionEditable";
import ContentCard from "../../../../../components/ui/cards/ContentCard";
import DocumentList from "../../../../../components/ui/forms/DocumentList";
import {
  SectionUploadButton,
  SectionMetaRow,
} from "../utils/SectionSharedComponents";

const CreditsDeductionsSection = ({
  filing,
  documents,
  isCpa,
  handleOpenUploadModal,
  handleDocumentDeleted,
  allEfilesComplete,
}) => {
  const ptfSituationForCredits = filing.tax_situation?.details?.PTF?.situation;
  if (!ptfSituationForCredits) return null;

  const taxYearsArrayForCredits = Array.isArray(
    ptfSituationForCredits?.taxYears,
  )
    ? ptfSituationForCredits.taxYears
    : ptfSituationForCredits?.taxYears
    ? [ptfSituationForCredits.taxYears]
    : [];
  const taxYearsNumericCredits = taxYearsArrayForCredits.map((year) =>
    Number(year),
  );

  const hasHomeOwnership =
    ptfSituationForCredits?.homeOwnership?.value === true;
  const hasEducation = ptfSituationForCredits?.esLoans?.value === true;
  const hasRetirement = ptfSituationForCredits?.rcDistributions?.value === true;
  const hasChildcare = ptfSituationForCredits?.cdcExpenses?.value === true;
  const hasCharitable = ptfSituationForCredits?.cdlmExpenses?.value === true;
  const hasNaturalDisaster =
    ptfSituationForCredits?.naturalDisaster?.value === true;

  const creditsMetrics = filing.summary_metrics?.credits_deductions_ptf || {};

  if (
    !(
      hasHomeOwnership ||
      hasEducation ||
      hasRetirement ||
      hasChildcare ||
      hasCharitable ||
      hasNaturalDisaster
    )
  )
    return null;

  const creditsSectionComplete = creditsMetrics.complete ?? false;
  const creditsSectionNeedsAttention =
    creditsMetrics.attention_needed_cus ?? false;
  const creditsIsWaiting =
    isCpa && !creditsSectionComplete && creditsSectionNeedsAttention;

  const getDocsByType = (docType) =>
    documents.filter((doc) => {
      const type = doc.documentType?.toUpperCase();
      const docYear = doc.documentYear ? Number(doc.documentYear) : null;
      return (
        type === docType &&
        docYear !== null &&
        taxYearsNumericCredits.includes(docYear)
      );
    });

  return (
    <AccordionEditable
      key="credits-deductions"
      title="Credits & Deductions"
      hasRequiredActions={!isCpa && creditsSectionNeedsAttention}
      allActionsComplete={creditsSectionComplete}
      isWaiting={creditsIsWaiting}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        {/* Home Ownership (Form 1098 – Mortgage Interest) */}
        {hasHomeOwnership && (
          <ContentCard>
            {(() => {
              const homeOwnershipMetrics = creditsMetrics["1098_home"] || {
                required: 0,
                provided: 0,
                complete: true,
              };
              const { required, provided, complete } = homeOwnershipMetrics;
              const needsAttention = !complete;
              const homeOwnershipDocuments = getDocsByType("1098_HOME");

              return (
                <>
                  <div className="section-header-row">
                    {needsAttention && (
                      <img
                        src={`${process.env.PUBLIC_URL}/images/icons/danger_not_filled.svg`}
                        alt="Attention needed"
                        className="attention-indicator-icon"
                      />
                    )}
                    <h4 className="section-header-title">
                      Home Ownership (Form 1098 – Mortgage Interest)
                    </h4>
                    <SectionUploadButton
                      show={!isCpa && needsAttention}
                      onClick={() =>
                        handleOpenUploadModal({
                          name: "homeOwnership",
                          label: "Form 1098 (Mortgage Interest)",
                          documentType: "1098_HOME",
                        })
                      }
                    />
                  </div>
                  <SectionMetaRow required={required} provided={provided} />
                  {homeOwnershipDocuments.length > 0 && (
                    <DocumentList
                      documents={homeOwnershipDocuments}
                      onDocumentDeleted={handleDocumentDeleted}
                      isCpa={isCpa}
                      disableDelete={!isCpa && allEfilesComplete}
                    />
                  )}
                </>
              );
            })()}
          </ContentCard>
        )}

        {/* Education / Student Loans */}
        {hasEducation && (
          <ContentCard>
            {(() => {
              const tuitionMetrics = creditsMetrics.education_tuition || {
                required: 0,
                provided: 0,
                complete: true,
              };
              const loanMetrics = creditsMetrics.student_loan_interest || {
                required: 0,
                provided: 0,
                complete: true,
              };
              const requiredCount =
                tuitionMetrics.required + loanMetrics.required;
              const providedCount =
                tuitionMetrics.provided + loanMetrics.provided;
              const needsAttention =
                !tuitionMetrics.complete || !loanMetrics.complete;

              const tuitionDocuments = getDocsByType("EDUCATION_TUITION");
              const loanInterestDocuments = getDocsByType(
                "STUDENT_LOAN_INTEREST",
              );
              const allDocs = [...tuitionDocuments, ...loanInterestDocuments];

              return (
                <>
                  <div className="section-header-row">
                    {needsAttention && (
                      <img
                        src={`${process.env.PUBLIC_URL}/images/icons/danger_not_filled.svg`}
                        alt="Attention needed"
                        className="attention-indicator-icon"
                      />
                    )}
                    <h4 className="section-header-title">
                      Education / Student Loans
                    </h4>
                  </div>
                  <SectionMetaRow
                    required={requiredCount}
                    provided={providedCount}
                  />
                  {allDocs.length > 0 && (
                    <DocumentList
                      documents={allDocs}
                      onDocumentDeleted={handleDocumentDeleted}
                      isCpa={isCpa}
                      disableDelete={!isCpa && allEfilesComplete}
                    />
                  )}
                  {!isCpa && needsAttention && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: "8px",
                        flexWrap: "wrap",
                      }}
                    >
                      {!tuitionMetrics.complete && (
                        <button
                          type="button"
                          className="cta-button primary"
                          style={{ width: "auto" }}
                          onClick={() =>
                            handleOpenUploadModal({
                              name: "esLoans",
                              label: "Form 1098-T (Tuition)",
                              documentType: "EDUCATION_TUITION",
                            })
                          }
                        >
                          Upload 1098-T
                        </button>
                      )}
                      {!loanMetrics.complete && (
                        <button
                          type="button"
                          className="cta-button primary"
                          style={{ width: "auto" }}
                          onClick={() =>
                            handleOpenUploadModal({
                              name: "esLoans",
                              label: "Form 1098-E (Student Loan Interest)",
                              documentType: "STUDENT_LOAN_INTEREST",
                            })
                          }
                        >
                          Upload 1098-E
                        </button>
                      )}
                    </div>
                  )}
                </>
              );
            })()}
          </ContentCard>
        )}

        {/* Retirement Contributions / Distributions */}
        {hasRetirement && (
          <ContentCard>
            {(() => {
              const contribMetrics = creditsMetrics.retirement_contribution || {
                required: 0,
                provided: 0,
                complete: true,
              };
              const distribMetrics = creditsMetrics.retirement_distribution || {
                required: 0,
                provided: 0,
                complete: true,
              };
              const requiredCount =
                contribMetrics.required + distribMetrics.required;
              const providedCount =
                contribMetrics.provided + distribMetrics.provided;
              const needsAttention =
                !contribMetrics.complete || !distribMetrics.complete;

              const contributionDocuments = getDocsByType(
                "RETIREMENT_CONTRIBUTION",
              );
              const distributionDocuments = getDocsByType(
                "RETIREMENT_DISTRIBUTION",
              );
              const allDocs = [
                ...contributionDocuments,
                ...distributionDocuments,
              ];

              return (
                <>
                  <div className="section-header-row">
                    {needsAttention && (
                      <img
                        src={`${process.env.PUBLIC_URL}/images/icons/danger_not_filled.svg`}
                        alt="Attention needed"
                        className="attention-indicator-icon"
                      />
                    )}
                    <h4 className="section-header-title">
                      Retirement Contributions / Distributions
                    </h4>
                  </div>
                  <SectionMetaRow
                    required={requiredCount}
                    provided={providedCount}
                  />
                  {allDocs.length > 0 && (
                    <DocumentList
                      documents={allDocs}
                      onDocumentDeleted={handleDocumentDeleted}
                      isCpa={isCpa}
                      disableDelete={!isCpa && allEfilesComplete}
                    />
                  )}
                  {!isCpa && needsAttention && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: "8px",
                        flexWrap: "wrap",
                      }}
                    >
                      {!contribMetrics.complete && (
                        <button
                          type="button"
                          className="cta-button primary"
                          style={{ width: "auto" }}
                          onClick={() =>
                            handleOpenUploadModal({
                              name: "rcDistributions",
                              label: "IRA/Roth/SEP Contribution",
                              documentType: "RETIREMENT_CONTRIBUTION",
                            })
                          }
                        >
                          Upload Contribution
                        </button>
                      )}
                      {!distribMetrics.complete && (
                        <button
                          type="button"
                          className="cta-button primary"
                          style={{ width: "auto" }}
                          onClick={() =>
                            handleOpenUploadModal({
                              name: "rcDistributions",
                              label: "Form 1099-R (Distribution)",
                              documentType: "RETIREMENT_DISTRIBUTION",
                            })
                          }
                        >
                          Upload 1099-R
                        </button>
                      )}
                    </div>
                  )}
                </>
              );
            })()}
          </ContentCard>
        )}

        {/* Childcare / Dependent Care Expenses */}
        {hasChildcare && (
          <ContentCard>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <h4 style={{ margin: 0, color: "#004789", fontSize: "16px" }}>
                Childcare / Dependent Care Expenses
              </h4>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                fontSize: "14px",
                color: "#1A2127",
              }}
            >
              {ptfSituationForCredits.cdcExpenses.fields
                ?.expensesTotalAmount && (
                <span>
                  Total Amount: $
                  {
                    ptfSituationForCredits.cdcExpenses.fields
                      .expensesTotalAmount
                  }
                </span>
              )}
            </div>
          </ContentCard>
        )}

        {/* Charitable Donations or Large Medical Expenses */}
        {hasCharitable && (
          <ContentCard>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <h4 style={{ margin: 0, color: "#004789", fontSize: "16px" }}>
                Charitable Donations or Large Medical Expenses
              </h4>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                fontSize: "14px",
                color: "#1A2127",
              }}
            >
              {ptfSituationForCredits.cdlmExpenses.fields
                ?.charitableDonationAmount && (
                <span>
                  Charitable Donation Amount: $
                  {
                    ptfSituationForCredits.cdlmExpenses.fields
                      .charitableDonationAmount
                  }
                </span>
              )}
              {ptfSituationForCredits.cdlmExpenses.fields
                ?.medicalExpensesAmount && (
                <span>
                  Medical Expenses Amount: $
                  {
                    ptfSituationForCredits.cdlmExpenses.fields
                      .medicalExpensesAmount
                  }
                </span>
              )}
            </div>
          </ContentCard>
        )}

        {/* Natural Disaster */}
        {hasNaturalDisaster && (
          <ContentCard>
            {(() => {
              const disasterMetrics = creditsMetrics.disaster_report || {
                required: 0,
                provided: 0,
                complete: true,
              };
              const { required, provided, complete } = disasterMetrics;
              const needsAttention = !complete;
              const naturalDisasterDocuments = getDocsByType("DISASTER_REPORT");

              return (
                <>
                  <div className="section-header-row">
                    {needsAttention && (
                      <img
                        src={`${process.env.PUBLIC_URL}/images/icons/danger_not_filled.svg`}
                        alt="Attention needed"
                        className="attention-indicator-icon"
                      />
                    )}
                    <h4 className="section-header-title">Natural Disaster</h4>
                    <SectionUploadButton
                      show={!isCpa && needsAttention}
                      onClick={() =>
                        handleOpenUploadModal({
                          name: "naturalDisaster",
                          label: "Natural Disaster",
                        })
                      }
                    />
                  </div>
                  <SectionMetaRow required={required} provided={provided} />
                  {naturalDisasterDocuments.length > 0 && (
                    <DocumentList
                      documents={naturalDisasterDocuments}
                      onDocumentDeleted={handleDocumentDeleted}
                      isCpa={isCpa}
                      disableDelete={!isCpa && allEfilesComplete}
                    />
                  )}
                </>
              );
            })()}
          </ContentCard>
        )}
      </div>
    </AccordionEditable>
  );
};

export default CreditsDeductionsSection;
